import React, {
    FC, useCallback, useEffect, useMemo, useRef,
} from 'react';
import { useFragment, useQuery, useReactiveVar } from '@apollo/client';
import { selectedPeriodVar } from 'app/apollo/reaction';
import { useCartMutationInBasket } from 'app/views/Basket/hooks/useCartMutationInBasket';
import {
    removeAddedDish,
    saveDishAddedPeriod,
} from 'app/views/Basket/components/BasketNewUpsell/components/BasketTopDishCard/addedPeriodStorage';
import { useAddedDishes } from 'app/views/Basket/hooks/useFurtherPeriodOrder';

import { FURTHER_PERIOD_CART_SECTIONS, FURTHER_PERIOD_DISHES } from '../../graphql/basketWithCustomization.graphql';
import { NewDishCard } from '../BasketDishCard/BasketDishCard';

import css from './furtherPeriodDishes.module.scss';

/**
 * @description период цикла хранится в формате YYYY-MM-DD,
 * в заголовке показываем его как DD.MM — например, 29.12
 */
export const formatFurtherPeriodDate = (periodDate: string) => (periodDate.length >= 10
    ? `${periodDate.slice(8, 10)}.${periodDate.slice(5, 7)}`
    : periodDate);

type TMutateItemInCart = (args: { // eslint-disable-line no-unused-vars
    dishes: { dish_id: string; portions: number }[];
    period?: string | null;
    optimistic?: string | null;
    onErrorCallback?: () => void;
}) => void;

type TProps = {
    // eslint-disable-next-line no-unused-vars
    onClickMobilePortions: (data: any) => void;
    // eslint-disable-next-line no-unused-vars
    openDishDetails: (dishId: string) => void;
    // eslint-disable-next-line no-unused-vars
    onHasContentChange: (hasContent: boolean) => void;
};

type TBlockProps = {
    // eslint-disable-next-line no-unused-vars
    period: string;
    // eslint-disable-next-line no-unused-vars
    onClickMobilePortions: (data: any) => void;
    // eslint-disable-next-line no-unused-vars
    openDishDetails: (dishId: string) => void;
    // eslint-disable-next-line no-unused-vars
    onBlockVisibilityChange: (blockPeriod: string, hasContent: boolean) => void;
};

type TBlockHook = {
    mutateItemInCart: TMutateItemInCart;
    isCartUpdating: boolean;
};

/**
 * @description блок одного доп. цикла: свой запрос корзины, свои хендлеры.
 *
 * Показывает всё, что лежит в корзине цикла. Источник истины - сама корзина, а не хранилище записей баннера.
 * Порции меняются и блюдо убираются строго из корзины своего периода:
 * mutateItemInCart без delivery_Id бьёт в обычную корзину (cart:new:<period>),
 * а period берётся из блока, а не из текущего selectedPeriod — текущий цикл
 * при этом не затрагивается
 */
const FurtherPeriodBlock: FC<TBlockProps> = ({
    period: furtherPeriod,
    onClickMobilePortions,
    openDishDetails,
    onBlockVisibilityChange,
}) => {
    const { mutateItemInCart, isCartUpdating } = useCartMutationInBasket() as TBlockHook;

    /* @note после перезагрузки корзины чужого цикла в кэше нет; при добавлении
     * блюда её дописывает мутация корзины, а cache-and-network подстраховывает
     * от случая, когда блюдо в этот цикл положили из меню и мутация записала
     * карту под другим id (без type) */
    useQuery(FURTHER_PERIOD_CART_SECTIONS, {
        variables: { period: furtherPeriod },
        fetchPolicy: 'cache-and-network',
    });

    const { data } = useFragment({
        fragment: FURTHER_PERIOD_DISHES,
        fragmentName: 'FURTHER_PERIOD_DISHES',
        from: {
            __ref: `cart:new:${furtherPeriod}`,
        },
    });

    const dishes = useMemo(
        () => (data?.sections || [])
            .reduce((acc: any[], section: any) => [...acc, ...(section?.items || [])], [])
            .filter((item: any) => Boolean(item?.dish_id) && item?.portions > 0),
        [data?.sections],
    );

    /* @note сообщаем родителю, есть ли в блоке блюда — по этому признаку
     * выше по дереву рисуется заголовок основного заказа */
    useEffect(() => {
        onBlockVisibilityChange?.(furtherPeriod, dishes.length > 0);
        return () => onBlockVisibilityChange?.(furtherPeriod, false);
    }, [dishes.length, furtherPeriod, onBlockVisibilityChange]);

    /* @note те же хендлеры, что и у основного списка, но с периодом доп. заказа.
     * Запись хранилища убираем сразу (её там может и не быть — блюдо добавили
     * из меню), чтобы список исчез мгновенно, а при ошибке возвращаем блюдо
     * на место */
    const handleRemoveDish = useCallback(
        (dishId: string) => {
            mutateItemInCart({
                dishes: [{ dish_id: dishId, portions: 0 }],
                period: furtherPeriod,
                onErrorCallback: () => saveDishAddedPeriod(dishId, furtherPeriod),
            });
            removeAddedDish(dishId);
        },
        [mutateItemInCart, furtherPeriod],
    );

    const handleChangePortion = useCallback(
        ({ id, value }: { id: string; value: number }) => {
            mutateItemInCart({
                dishes: [{ dish_id: id, portions: value }],
                period: furtherPeriod,
                optimistic: 'portion',
            });
        },
        [mutateItemInCart, furtherPeriod],
    );

    if (!dishes.length) return null;

    return (
        <div className={css.furtherPeriodDishesRoot}>
            <div className={css.furtherPeriodDishesHeaderContainer}>
                <div className={`body-l-semibold ${css.furtherPeriodDishesHeading}`}>
                    Заказ на Хэллоуин
                </div>
                <div className={`body-s-medium ${css.furtherPeriodDishesTitle}`}>
                    {`доставка с ${formatFurtherPeriodDate(furtherPeriod)} | оплата в ЛК или курьеру`}
                </div>
            </div>
            <div className={css.furtherPeriodDishesList}>
                {dishes.map((dish: any) => (
                    <NewDishCard
                        key={`further-${dish.dish_id}`}
                        cacheRef={`cartItem:${dish.dish_id}`}
                        handleChangePortion={handleChangePortion}
                        handleRemoveDish={handleRemoveDish}
                        onClickMobilePortions={onClickMobilePortions}
                        openDishDetails={openDishDetails}
                        mutateItemInCart={mutateItemInCart}
                        isCartUpdating={isCartUpdating}
                        period={furtherPeriod as any}
                        canModifyDish={false}
                    />
                ))}
            </div>
        </div>
    );
};

/**
 * @description блюда, лежащие в корзинах невыбранных циклов: их не видно в основном
 * списке (он читает корзину текущего периода), а на чекауте по ним создаётся
 * отдельный инвойс.
 *
 * Показываются только циклы, в которые блюдо добавили с баннера топ-блюда:
 * запись об этом лежит в sessionStorage, по ней же чекаут создаёт доп. инвойс.
 * Блюдо, положенное в следующий цикл из меню, здесь не показываем — на него
 * доп. инвойс не создаётся.
 * @note блюда группируются по периодам: у каждого цикла свой блок со своей
 * корзиной, чтобы порции и удаление не уходили в чужой период
 */
const FurtherPeriodDishes: FC<TProps> = ({
    onClickMobilePortions,
    openDishDetails,
    onHasContentChange,
}) => {
    const period = useReactiveVar(selectedPeriodVar);
    const addedDishes = useAddedDishes();

    const blockVisibilityRef = useRef<Record<string, boolean>>({});

    const handleBlockVisibilityChange = useCallback(
        (blockPeriod: string, hasContent: boolean) => {
            blockVisibilityRef.current[blockPeriod] = hasContent;
            onHasContentChange?.(Object.values(blockVisibilityRef.current).some(Boolean));
        },
        [onHasContentChange],
    );

    const furtherPeriods = useMemo(() => {
        const candidates = new Set<string>();

        addedDishes
            .filter(({ period: addedPeriod }) => Boolean(addedPeriod) && addedPeriod !== period)
            .forEach(({ period: addedPeriod }) => candidates.add(addedPeriod));

        return Array.from(candidates).sort();
    }, [addedDishes, period]);

    if (!furtherPeriods.length) return null;

    return (
        <>
            {furtherPeriods.map((blockPeriod) => (
                <FurtherPeriodBlock
                    key={blockPeriod}
                    period={blockPeriod}
                    onClickMobilePortions={onClickMobilePortions}
                    openDishDetails={openDishDetails}
                    onBlockVisibilityChange={handleBlockVisibilityChange}
                />
            ))}
        </>
    );
};

export default FurtherPeriodDishes;
